import { CELESTIAL_SOLAR, HALFMOON_SOLAR } from '~/components/image';
import { SmbScreenMap } from '~/machines/smb-funnel/screen-map';
import { ScreenMapCopy } from '~/machines/utils/types';
import { BillingContext, BillingCopy } from '~/screens/billing';
import { CreateAccountCopy } from '~/screens/create-account';
import { CredentialsContext, CredentialsCopy } from '~/screens/credentials';
import { SelectUtilityCopy } from '~/screens/select-utility';
import { SmbTermsContext, SmbTermsCopy } from '~/screens/smb-terms';
import { UTILITY_IDS } from '~/utils/constants';

const isVersantBHD = (id: number) => id === UTILITY_IDS['Versant - Bangor Hydro District'];
const isCMP = (id: number) => id === UTILITY_IDS['Central Maine Power'];

export const copy: ScreenMapCopy<SmbScreenMap> = {
  utility: {
    utility: (): Partial<SelectUtilityCopy> => ({
      title: "Let's get your business enrolled in community solar.",
      description:
        'Thanks for your interest! Community solar is the easiest, most direct way to add more clean energy to your local power supply without changing your electric utility.',
      descriptionTwo: 'To start, confirm the electric utility for your business address.',
      zipCodeLabel: 'Business service area zip code',
      zipCodePlaceholder: 'Enter the zip code where your business is located',
    }),
  },
  user: {
    user: (): Partial<CreateAccountCopy> => ({
      title: 'Now, create an account for your business.',
      description:
        "You'll use this to manage your community solar subscription and stay informed about updates.",
      imageType: CELESTIAL_SOLAR,
      learnMoreModal: undefined,
    }),
  },
  enrollment: {
    utilityConnect: (context: CredentialsContext): Partial<CredentialsCopy> => {
      return {
        title: "Next, sync your business's utility account.",
        description: `We’ll securely connect to ${context.utility.displayName} to verify your eligibility and keep track of the monthly savings you earn with community solar.`,
        disclaimer:
          'Arcadia is an independent service provider committed to putting you and the planet first. Arcadia is not affiliated with any utility or governmental agency.',
        learnMoreModal: undefined,
        imageType: HALFMOON_SOLAR,
      };
    },
  },
  billing: {
    payment: (context: BillingContext): Partial<BillingCopy> => {
      const utilityId = context.utility.id;
      const descriptions = {
        acb: "You'll pay your business's utility bill through our secure platform so we can directly apply your monthly community solar savings.",
        dual: "You'll pay your utility company for your power bill as you normally do. For your contribution to the solar farm, we'll charge your payment method on file three days after you receive your Arcadia statement. This is always an automatic payment, so no action is needed on your end. We'll route the funds to the solar developers to help maintain the solar farm.",
        ucb: '',
      };

      const overrides: Partial<BillingCopy> = {
        title: 'Then add a payment method',
        description: descriptions[context.experience!],
        learnMoreModal: undefined,
      };

      if (isVersantBHD(utilityId) || isCMP(utilityId)) {
        overrides.payArcadiaText =
          "We'll send you a bill for your contribution to the solar farm — an automatic payment that helps keep your solar farm running. You'll see your solar savings of up to 15% here.";
      }
      return overrides;
    },
  },
  legal: {
    review: (context: SmbTermsContext): Partial<SmbTermsCopy> => {
      const utility = context.utility.displayName;
      const experience = {
        ucb: {
          utilityBill: `You’ll continue paying your bill like you do now. You’ll get a monthly statement from ${utility} that includes your utility charges and solar credits.`,
        },
        acb: {
          utilityBill: `You’ll pay your monthly ${utility} bill through our secure platform. Arcadia will then pay the solar farm and your utility. You’ll see your monthly savings applied to your bill.`,
        },
        dual: {
          utilityBill: `You’ll pay your ${utility} bill just like you do now and you’ll pay your contribution to the solar farm through Arcadia’s secure platform.`,
        },
      }[context.billingSelection!];

      return {
        ...experience,
      };
    },
  },
};
